import type { KeyboardEvent, RefObject } from "react"

import { useEffect, useLayoutEffect, useRef, useState } from "react"

import type { GuildWarCandidatePoolMember } from "@/core/guild-war/types"
import type { TeamBoardState } from "@/lib/team-board-store"

import { Button } from "@/components/button"
import { RosterExportCard } from "@/components/guild-war/roster-export-card"
import { StatusAlert } from "@/components/shared/status-alert"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

export function TeamBoardExportPreviewDialog({
  open,
  isExporting,
  exportError,
  board,
  candidateById,
  displayDateLabel,
  dialogRef,
  onClose,
  onDownload,
  onDialogKeyDown,
}: {
  open: boolean
  isExporting: boolean
  exportError: string | null
  board: TeamBoardState
  candidateById: Map<string, GuildWarCandidatePoolMember>
  displayDateLabel: string
  dialogRef: RefObject<HTMLDivElement | null>
  onClose: () => void
  onDownload: () => void
  onDialogKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void
}) {
  const previewViewportRef = useRef<HTMLDivElement | null>(null)
  const previewContentRef = useRef<HTMLDivElement | null>(null)
  const [previewScale, setPreviewScale] = useState(1)
  const [isPreviewReady, setIsPreviewReady] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }

    setIsPreviewReady(false)
  }, [open])

  useLayoutEffect(() => {
    if (!open) {
      return
    }
    let cancelled = false
    let animationFrameId: number | null = null
    let observer: ResizeObserver | null = null

    const setup = () => {
      if (cancelled) {
        return
      }

      const viewport = previewViewportRef.current
      const content = previewContentRef.current

      if (!viewport || !content) {
        animationFrameId = requestAnimationFrame(setup)
        return
      }

      const updateScale = () => {
        const contentWidth = content.scrollWidth
        const contentHeight = content.scrollHeight
        const availableWidth = viewport.clientWidth
        const availableHeight = viewport.clientHeight

        if (
          contentWidth <= 0 ||
          contentHeight <= 0 ||
          availableWidth <= 0 ||
          availableHeight <= 0
        ) {
          setPreviewScale(1)
          setIsPreviewReady(true)
          return
        }

        const widthScale = availableWidth / contentWidth
        const heightScale = availableHeight / contentHeight
        setPreviewScale(Math.min(widthScale, heightScale, 1))
        setIsPreviewReady(true)
      }

      updateScale()
      observer = new ResizeObserver(updateScale)
      observer.observe(viewport)
      observer.observe(content)
    }

    setup()

    return () => {
      cancelled = true
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
      }
      observer?.disconnect()
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose()
        }
      }}
    >
      <DialogContent
        ref={dialogRef}
        onKeyDown={onDialogKeyDown}
        showCloseButton={false}
        className="z-9999 flex h-[92vh] w-full max-w-375 flex-col gap-0 overflow-hidden border border-border bg-background p-0 shadow-2xl sm:max-w-375"
      >
        <DialogHeader className="flex-row items-center justify-between border-b border-border px-6 py-4">
          <div>
            <DialogTitle
              id="team-board-export-preview-title"
              className="text-xl"
            >
              Export Preview
            </DialogTitle>
            <DialogDescription
              id="team-board-export-preview-description"
              className="mt-1"
            >
              This is the dedicated image layout that will be downloaded as a
              PNG.
            </DialogDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onDownload}
              disabled={isExporting}
            >
              {isExporting ? "Exporting..." : "Download PNG"}
            </Button>
            <Button variant="ghost" data-export-preview-close onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogHeader>

        {exportError ? (
          <StatusAlert tone="danger" className="mx-6 mt-4">
            {exportError}
          </StatusAlert>
        ) : null}
        <div className="min-h-0 flex-1 overflow-hidden bg-muted/30 p-4 md:p-6">
          <div
            ref={previewViewportRef}
            className="relative size-full overflow-hidden"
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                ref={previewContentRef}
                style={{
                  transform: `scale(${previewScale})`,
                  transformOrigin: "center center",
                }}
                className={`transition-opacity duration-150 will-change-transform ${
                  isPreviewReady ? "opacity-100" : "opacity-0"
                }`}
              >
                <RosterExportCard
                  displayDateLabel={displayDateLabel}
                  board={board}
                  candidateById={candidateById}
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
